import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import GoogleMapReact from 'google-map-react';
import { Navigation, MapPin, QrCode, FileText, ClipboardList, Search } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import Header from '../components/Dashboard/Header';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Tabs from '../components/ui/Tabs';
import Modal from '../components/ui/Modal';
import DataTable from '../components/ui/DataTable';
import { PageLoader } from '../components/ui/Spinner';

import IncidentMap from '../components/Incident/IncidentMap';
import IncidentMarker from '../components/Incident/IncidentMarker';
import METHANE from '../components/Incident/METHANE';
import QuickActions from '../components/Incident/QuickActions';
import ExactLocation from '../components/Announcement/ExactLocation';

import AnnouncementService from '../services/AnnouncementService';
import AnnouncementMemberService from '../services/AnnouncementMemberService';
import AnnouncementHospitalService from '../services/AnnouncementHospitalService';
import AuthService from '../services/AuthService';
import TeamsService from '../services/TeamsService';
import TeamMemberService from '../services/TeamMemberService';
import HospitalFacilityService from '../services/HospitalFacilityService';
import PatientService from '../services/PatientService';
import getSocket from '../services/socket';
import TeamCard from '../components/Incident/TeamCard';
import MemberCard from '../components/Teams/MemberCard';
import ResponseSummary from '../components/Incident/ResponseSummary';
import ResponderRoster from '../components/Incident/ResponderRoster';
import HospitalRoster from '../components/Incident/HospitalRoster';
import TeamLeaderPicker from '../components/Incident/TeamLeaderPicker';
import { processTeamData, haversineDistanceKm } from '../utils/utils';
import { useThemeMode } from '../context/ThemeContext';
import { DARK_MAP_STYLE, LIGHT_MAP_STYLE } from '../config/mapTheme';

const INCIDENT_TABS = [
    { value: 'map', label: 'Map & Location' },
    { value: 'teams', label: 'Teams' },
    { value: 'patients', label: 'Patients / Decon' },
    { value: 'hospitals', label: 'Hospitals' },
    { value: 'handoff', label: 'Handoff / Reports' },
    { value: 'history', label: 'History' },
];

// Maps the audit-log "action" string (e.g. "announcement.close") to a readable label.
// "announcement.activate" covers both the first activation and any later reopen —
// the surrounding entries (a Closed row just before it) make that distinction obvious
// without needing a separate action name.
const HISTORY_LABELS = {
    'announcement.create': 'Created',
    'announcement.activate': 'Activated',
    'announcement.close': 'Closed',
    'announcement.reject': 'Rejected',
    'announcement.team-leader': 'Team leader assigned',
};

const ROLE_LABELS = { admins: 'Admin', doctors: 'Doctor', nurses: 'Nurse', rsos: 'RSO' };

// Hot = contaminated (arrival), Warm = decon, Cold = clean — explicit, since Badge's
// text heuristic would color these arbitrarily.
const ZONE_VARIANTS = { Hot: 'red', Warm: 'amber', Cold: 'green' };

// Where each patient is in the RSO decon loop — same states and colours as the tablet.
// Untriaged patients haven't reached the RSO yet.
const RSO_VARIANTS = { 'Awaiting triage': 'neutral', 'Awaiting screening': 'amber', 'In decon': 'blue', Cleared: 'green', Isolation: 'red' };
const rsoStatusOf = (row) => (row.triagedAt ? row.rsoStage || 'Awaiting screening' : 'Awaiting triage');

function historyNotes(details) {
    if (!details) return null;
    try {
        const parsed = JSON.parse(details);
        return parsed.notes || parsed.closeNotes || parsed.reason || null;
    } catch {
        return null;
    }
}

function Incident() {
    let { id } = useParams();
    const navigate = useNavigate();
    const { mode } = useThemeMode();
    const [announcement, setAnnouncement] = useState({});
    const [members, setMembers] = useState([]);
    const [patients, setPatients] = useState([]);
    const [teams, setTeams] = useState([]);
    const [assignedTeams, setAssignedTeams] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [action, setAction] = useState("assignTeam");
    const [tab, setTab] = useState('map');
    const [assignResponderOpen, setAssignResponderOpen] = useState(false);
    const [allMembers, setAllMembers] = useState([]);
    const [loadingAllMembers, setLoadingAllMembers] = useState(false);
    const [responderSearch, setResponderSearch] = useState('');
    const [selectedResponderIds, setSelectedResponderIds] = useState([]);
    const [assigningResponders, setAssigningResponders] = useState(false);
    const [hospitals, setHospitals] = useState([]);
    const [assignHospitalOpen, setAssignHospitalOpen] = useState(false);
    const [activationGuardOpen, setActivationGuardOpen] = useState(false);
    const [allHospitals, setAllHospitals] = useState([]);
    const [loadingAllHospitals, setLoadingAllHospitals] = useState(false);
    const [hospitalSearch, setHospitalSearch] = useState('');
    const [selectedHospitalIds, setSelectedHospitalIds] = useState([]);
    const [assigningHospitals, setAssigningHospitals] = useState(false);
    const [closeOpen, setCloseOpen] = useState(false);
    const [closeNotes, setCloseNotes] = useState('');
    const [reopenOpen, setReopenOpen] = useState(false);
    const [reopenNotes, setReopenNotes] = useState('');
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activating, setActivating] = useState(false);
    const [distances, setDistances] = useState(null);
    const [locationPickerOpen, setLocationPickerOpen] = useState(false);
    const [pendingLocation, setPendingLocation] = useState(null); // { location, lat, lng }
    const [resolvingAddress, setResolvingAddress] = useState(false);
    const [savingLocation, setSavingLocation] = useState(false);

    const STATUS_BADGE = { Pending: 'amber', Active: 'green', Closed: 'neutral', Rejected: 'red' };
    // Approving (activating/reopening) or standing down (closing) a CBRNE case is a
    // higher-command decision — whoever created it (as low as Team Leader) can
    // view/manage it but can't also be the one who approves or closes it. Mirrored
    // server-side (requireCommand on /activate, /close and /reject in
    // AnnouncementRoute.js).
    const canCommand = ['Incident Commander', 'Admin'].includes(AuthService.getUser()?.accessTier);
    // Team/responder/roster assignment is Team Leader+ server-side (see app.js's
    // adminOrAccessTierWriteGuard on /api/announcement-member) — this page itself is
    // visible down to Medical Staff, so any action tied to that narrower write
    // permission needs its own gate rather than being shown to everyone who can just
    // view the incident.
    const canManage = ['Team Leader', 'Incident Commander', 'Admin', 'High Management'].includes(AuthService.getUser()?.accessTier);

    const openLocationPicker = () => {
        setPendingLocation({
            location: announcement.location || '',
            lat: announcement.lat ? Number.parseFloat(announcement.lat) : 24.491241,
            lng: announcement.lng ? Number.parseFloat(announcement.lng) : 54.37831961,
        });
        setLocationPickerOpen(true);
    };

    // Clicking the map drops the pin there and reverse-geocodes it to a readable
    // address, same as picking a search result does.
    const handlePickOnMap = ({ lat, lng }) => {
        setPendingLocation((prev) => ({ ...prev, lat, lng }));
        if (!window.google?.maps) return;
        setResolvingAddress(true);
        new window.google.maps.Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
            setResolvingAddress(false);
            if (status === 'OK' && results?.[0]) {
                setPendingLocation((prev) => ({ ...prev, location: results[0].formatted_address }));
            }
        });
    };

    const handleSaveLocation = async () => {
        if (!pendingLocation) return;
        setSavingLocation(true);
        try {
            await AnnouncementService.update({ ...announcement, ...pendingLocation });
            await loadAnnouncement();
            setLocationPickerOpen(false);
            toast.success('Location updated.');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save location.');
        } finally {
            setSavingLocation(false);
        }
    };

    // Distance of each responding member from the incident, nearest first — reuses the
    // same haversine calc TeamLeaderView's roster already uses.
    const handleCalculateDistance = () => {
        if (!announcement.lat || !announcement.lng) {
            toast.error('Incident has no coordinates yet.');
            return;
        }
        const incidentLat = Number.parseFloat(announcement.lat);
        const incidentLng = Number.parseFloat(announcement.lng);
        const results = members
            .filter((m) => m.lat && m.lng)
            .map((m) => ({
                id: m.id,
                name: m.member?.fullname || 'Unknown',
                team: m.team?.name || '—',
                distanceKm: haversineDistanceKm(Number.parseFloat(m.lat), Number.parseFloat(m.lng), incidentLat, incidentLng),
            }))
            .sort((a, b) => a.distanceKm - b.distanceKm);
        setDistances(results);
        if (results.length === 0) toast.error('No responding members have a location yet.');
    };

    // Neither the client nor the server ever refused to activate an incident with an
    // empty roster — escalateAnnouncementTeams/notifyAssignedMembers just find nothing
    // to do and no-op, so an Incident Commander could go live with zero teams and zero
    // hospitals dispatched and the only sign anything was wrong was a toast reading
    // "0 member(s) notified." Guard client-side instead of silently letting that through.
    const handleActivate = async () => {
        if (activating) return;
        if (assignedTeams.length === 0 || hospitals.length === 0 || !announcement.teamLeader) {
            setActivationGuardOpen(true);
            return;
        }
        setActivating(true);
        try {
            const { data } = await AnnouncementService.activate(id);
            toast.success(`Event activated. ${data.notified || 0} member(s) notified.`);
            loadAnnouncement();
            loadHistory();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Activation failed.');
        } finally {
            setActivating(false);
        }
    };

    const handleReopen = async () => {
        try {
            const { data } = await AnnouncementService.activate(id, reopenNotes);
            toast.success(`Event reopened. ${data.notified || 0} member(s) notified.`);
            setReopenOpen(false);
            setReopenNotes('');
            loadAnnouncement();
            loadHistory();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Reopen failed.');
        }
    };

    const handleClose = async () => {
        try {
            await AnnouncementService.close(id, closeNotes);
            toast.success('Event closed.');
            setCloseOpen(false);
            loadAnnouncement();
            loadHistory();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Close failed.');
        }
    };

    const loadMembers = async () => {
        const response = await AnnouncementMemberService.findById(id);

        if (response.error) {
            toast.error("Something went wrong!");
            return;
        }
        console.log("members", response.entity);
        setMembers(response.entity);

        // Extract unique teams (filter out null teams)
        const uniqueTeams = response.entity
            .map(item => item.team)
            .filter(Boolean)
            .filter((team, index, self) =>
                index === self.findIndex(t => t.id === team.id)
            );

        // Process each team to get the required format
        const processedTeams = uniqueTeams.map(team => {
            // Filter members belonging to the current team
            const teamMembers = response.entity.filter(item => item.team && item.team.id === team.id);

            // Count total members and members by role
            const roleCounts = teamMembers.reduce((acc, item) => {
                acc.total += 1;
                const role = item.member.role.role_name.toLowerCase();
                if (acc[role]) {
                    acc[role] += 1;
                } else {
                    acc[role] = 1;
                }
                return acc;
            }, { total: 0 });

            return {
                id: team.id,
                name: team.name,
                totalMembers: roleCounts.total,
                admins: roleCounts.admins || 0,
                doctors: roleCounts.doctors || 0,
                nurses: roleCounts.nurses || 0,
                rsos: roleCounts.rsos || 0
            };
        });

        console.log("processed members", processedTeams);
        setAssignedTeams(processedTeams)
    };

    const loadPatients = async () => {
        try {
            const { entity } = await PatientService.findByAnnouncement(id);
            setPatients(entity || []);
        } catch (err) {
            console.log(err);
        }
    };

    const loadHospitals = async () => {
        try {
            const { entity } = await AnnouncementHospitalService.findByAnnouncement(id);
            setHospitals(entity || []);
        } catch (err) {
            console.log(err);
        }
    };

    const loadHistory = async () => {
        try {
            const { data } = await AnnouncementService.getHistory(id);
            setHistory(data || []);
        } catch (err) {
            console.log(err);
        }
    };

    const loadAnnouncement = async () => {
        const data = await AnnouncementService.findById(id);

        if (data.data.error) {
            toast.error("Something went wrong!");
            return;
        }
        setAnnouncement(data.data);

    };

    const handelViewTeam = (id) => {
        // eslint-disable-next-line eqeqeq
        const data = members.filter((item) => item.teamId == id);
        setTeamMembers(data)
        setAction("viewMembers")
    }

    // Split out of handleAssignTeam so the full org team list can also be prefetched on
    // page load (see the mount effect below) — previously this only ever fetched when
    // the user clicked "Assign Team", so the picker sat empty/loading on first open.
    const loadTeams = async () => {
        try {
            const response = await TeamsService.findAll();
            if (response.data.error) {
                toast.error('Something went wrong!');
                return;
            }
            setTeams(processTeamData(response.data));
        } catch (error) {
            console.log(error);
            toast.error('Failed to load teams.');
        }
    };

    const handleAssignTeam = async () => {
        await loadTeams();
        setAction('viewTeam');
    };

    // After a team is assigned+notified, refresh the roster so the newly
    // assigned team shows up immediately instead of waiting for the next poll.
    const refreshAndShowAssigned = async () => {
        await loadMembers();
        setAction('assignTeam');
    };

    // Individual responder assignment — separate from team-level ActivateAll, for
    // pulling in specific people (e.g. a subject-matter expert) without their whole team.
    const openAssignResponder = async () => {
        setAssignResponderOpen(true);
        setSelectedResponderIds([]);
        setResponderSearch('');
        setLoadingAllMembers(true);
        try {
            const response = await TeamMemberService.findAll();
            setAllMembers(response.data || []);
        } catch (err) {
            console.log(err);
            toast.error('Failed to load members.');
        } finally {
            setLoadingAllMembers(false);
        }
    };

    const toggleResponder = (memberId) => {
        setSelectedResponderIds((prev) =>
            prev.includes(memberId) ? prev.filter((mid) => mid !== memberId) : [...prev, memberId]
        );
    };

    const handleAssignResponders = async () => {
        if (selectedResponderIds.length === 0) return;
        setAssigningResponders(true);
        try {
            const data = await AnnouncementMemberService.assignMembers(id, selectedResponderIds);
            if (data.error) {
                toast.error(data.message || 'Something went wrong!');
                return;
            }
            toast.success(`${selectedResponderIds.length} responder(s) assigned.`);
            setAssignResponderOpen(false);
            await loadMembers();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to assign responder(s).');
        } finally {
            setAssigningResponders(false);
        }
    };

    const assignedMemberIds = new Set(members.map((m) => m.member?.id).filter(Boolean));
    const availableResponders = allMembers
        .filter((m) => !assignedMemberIds.has(m.id))
        .filter((m) => m.fullname?.toLowerCase().includes(responderSearch.toLowerCase()));

    // Direct hospital assignment — separate from a team's home hospital, which only
    // gets involved indirectly once that team is assigned to the incident.
    const openAssignHospital = async () => {
        setAssignHospitalOpen(true);
        setSelectedHospitalIds([]);
        setHospitalSearch('');
        setLoadingAllHospitals(true);
        try {
            const response = await HospitalFacilityService.findAll();
            setAllHospitals(response?.data || []);
        } catch (err) {
            console.log(err);
            toast.error('Failed to load hospitals.');
        } finally {
            setLoadingAllHospitals(false);
        }
    };

    const toggleHospital = (hospitalId) => {
        setSelectedHospitalIds((prev) =>
            prev.includes(hospitalId) ? prev.filter((hid) => hid !== hospitalId) : [...prev, hospitalId]
        );
    };

    const handleAssignHospitals = async () => {
        if (selectedHospitalIds.length === 0) return;
        setAssigningHospitals(true);
        try {
            const data = await AnnouncementHospitalService.assign(id, selectedHospitalIds);
            if (data.error) {
                toast.error(data.message || 'Something went wrong!');
                return;
            }
            toast.success(`${selectedHospitalIds.length} hospital(s) assigned.`);
            setAssignHospitalOpen(false);
            await loadHospitals();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to assign hospital(s).');
        } finally {
            setAssigningHospitals(false);
        }
    };

    const assignedHospitalIds = new Set(hospitals.map((h) => h.hospitalFacilityId));
    const availableHospitals = allHospitals
        .filter((h) => !assignedHospitalIds.has(h.id))
        .filter((h) => h.name?.toLowerCase().includes(hospitalSearch.toLowerCase()));

    // useEffect(() => {
    //     loadAnnouncement();
    //     loadMembers();
    // }, [id]);



    useEffect(() => {
        // Load announcements initially
        Promise.all([loadAnnouncement(), loadMembers(), loadPatients(), loadHistory(), loadHospitals(), loadTeams()]).finally(() => setLoading(false));

        // Members/hospitals still poll — only the incident record and the patient
        // board are live-pushed for now (see below).
        const intervalId = setInterval(() => {
            loadMembers();
            loadHospitals();
        }, 30000);

        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Skip the "No teams to display" dead-end + its manual "Assign Team" click on first
    // load when this incident has no teams yet — go straight to the assignable-team
    // picker instead. Guarded to fire only once (autoSwitchedToAssign ref) so the 30s
    // members poll above doesn't keep yanking the view back to "viewTeam" if the user
    // has since navigated elsewhere (e.g. Manage Roster) while still team-less.
    const autoSwitchedToAssign = useRef(false);
    useEffect(() => {
        if (loading || autoSwitchedToAssign.current) return;
        if (assignedTeams.length === 0) {
            autoSwitchedToAssign.current = true;
            setAction('viewTeam');
        }
    }, [loading, assignedTeams]);

    // Live incident + patient-board updates — see server src/realtime/emit.js.
    // Scoped to this incident's room so a client isn't flooded with every incident's
    // patient traffic system-wide, only the one currently open.
    useEffect(() => {
        const socket = getSocket();
        socket.emit('incident:join', id);

        const onIncidentUpdate = (incident) => {
            if (String(incident.id) === String(id)) setAnnouncement(incident);
        };
        const onPatientChange = (patient) => {
            setPatients((prev) => {
                const idx = prev.findIndex((p) => p.recordId === patient.recordId);
                if (idx === -1) return [patient, ...prev];
                const next = [...prev];
                next[idx] = patient;
                return next;
            });
        };

        socket.on('incident:update', onIncidentUpdate);
        socket.on('patient:create', onPatientChange);
        socket.on('patient:update', onPatientChange);

        return () => {
            socket.emit('incident:leave', id);
            socket.off('incident:update', onIncidentUpdate);
            socket.off('patient:create', onPatientChange);
            socket.off('patient:update', onPatientChange);
        };
    }, [id]);

    if (loading) {
        return (
            <Sidebar title="Incident" header={<Header />}>
                <PageLoader label="Loading incident…" />
            </Sidebar>
        );
    }

    return (
        <Sidebar title={`${announcement.drill ? "Drill" : "Announcement"}- ${announcement.title}`} header={<Header />}>
            <Card padded={false} className="overflow-hidden">
                <div className="px-6 py-5 border-b border-neutral-100 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 dark:border-neutral-800">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            {announcement.id != null && <Badge variant="neutral">#{announcement.id}</Badge>}
                            {announcement.incidentTypeNames?.map((name) => (
                                <Badge key={name} variant="red">{name}</Badge>
                            ))}
                            <Badge variant={announcement.drill ? 'amber' : 'green'}>
                                {announcement.drill ? 'Drill' : 'Live Incident'}
                            </Badge>
                            {announcement.status && (
                                <Badge variant={STATUS_BADGE[announcement.status] || 'neutral'}>{announcement.status}</Badge>
                            )}
                        </div>
                        <h3 className="font-bold text-xl text-neutral-900 dark:text-neutral-50">{announcement.title || 'Incident'}</h3>
                        <p className="text-sm text-neutral-500 mt-1 dark:text-neutral-500">
                            {announcement.description || 'Interactive navigation for METHANE, map, teams, patients, decon, stakeholders and reporting.'}
                        </p>
                    </div>
                    <div className="flex gap-3">
                        {announcement.status === 'Pending' && (
                            canCommand ? (
                                <Button variant="primary" onClick={handleActivate} disabled={activating}>
                                    {activating ? 'Activating…' : 'Activate & Notify Teams'}
                                </Button>
                            ) : (
                                <Badge variant="amber">Pending — Awaiting Command Approval</Badge>
                            )
                        )}
                        {announcement.status === 'Active' && canCommand && (
                            <Button variant="secondary" onClick={() => setCloseOpen(true)}>
                                Close Event
                            </Button>
                        )}
                        {(announcement.status === 'Closed' || announcement.status === 'Rejected') && (
                            <div className="flex items-center gap-3">
                                <Badge variant="neutral">
                                    {announcement.status}
                                    {announcement.status === 'Closed' && announcement.closedAt
                                        ? ` ${new Date(announcement.closedAt).toLocaleString()}`
                                        : ''}
                                </Badge>
                                {canCommand && (
                                    <Button variant="secondary" onClick={() => setReopenOpen(true)}>
                                        Reopen Event
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-6 py-3.5 border-b border-neutral-100 dark:border-neutral-800">
                    <METHANE announcement={announcement} />
                </div>

                <div className="p-6">
                    <Tabs tabs={INCIDENT_TABS} active={tab} onChange={setTab} className="mb-6" />

                    {tab === 'map' && (
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                            <div className="xl:col-span-2 rounded-xl border border-neutral-200 overflow-hidden relative h-[420px] dark:border-neutral-700">
                                <IncidentMap
                                    lat={announcement.lat}
                                    lng={announcement.lng}
                                    members={members}
                                />
                            </div>
                            <div className="space-y-3">
                                <div className="text-sm text-neutral-600 dark:text-neutral-300">
                                    <b className="text-neutral-900 dark:text-neutral-50">Exact Location:</b> {announcement.location || '—'}
                                </div>
                                <div className="text-sm text-neutral-600 dark:text-neutral-300">
                                    <b className="text-neutral-900 dark:text-neutral-50">Coordinates:</b> {announcement.lat ?? '—'}, {announcement.lng ?? '—'}
                                </div>
                                <div className="text-sm text-neutral-600 dark:text-neutral-300">
                                    <b className="text-neutral-900 dark:text-neutral-50">Nearest Landmark:</b> {announcement.landmark || '—'}
                                </div>
                                <button className="action-button" onClick={openLocationPicker}>
                                    <Navigation className="w-4 h-4 mr-2" /> Capture Google Coordinates
                                </button>
                                <button className="action-button" onClick={handleCalculateDistance}>
                                    <MapPin className="w-4 h-4 mr-2" /> Calculate Team Distance
                                </button>

                                {distances && (
                                    <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100 overflow-hidden dark:border-neutral-700 dark:divide-neutral-800">
                                        {distances.length === 0 ? (
                                            <p className="text-xs text-neutral-400 text-center py-4 dark:text-neutral-500">No located responders yet.</p>
                                        ) : (
                                            distances.map((d) => (
                                                <div key={d.id} className="flex items-center justify-between px-3 py-2 text-xs">
                                                    <span className="text-neutral-700 dark:text-neutral-300">{d.name} <span className="text-neutral-400 dark:text-neutral-500">· {d.team}</span></span>
                                                    <span className="font-semibold text-neutral-900 dark:text-neutral-50">{d.distanceKm.toFixed(1)} km</span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {tab === 'teams' && (
                        <div>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                                <div>
                                    <h4 className="font-bold text-neutral-900 dark:text-neutral-50">Team Assignment &amp; Response</h4>
                                    <p className="text-xs text-neutral-500 mt-1 dark:text-neutral-500">Assign responding teams and track member acceptance.</p>
                                </div>
                                <QuickActions setTeams={setTeams} announcementId={id} setAction={setAction} onAssignTeam={handleAssignTeam} onAssignResponder={openAssignResponder} onManageRoster={() => setAction('roster')} onAssignHospital={openAssignHospital} canManage={canManage} />
                            </div>

                            <TeamLeaderPicker
                                announcementId={id}
                                teamLeader={announcement.teamLeader}
                                members={members}
                                canManage={canManage}
                                locked={['Closed', 'Rejected'].includes(announcement.status)}
                                onChanged={() => { loadAnnouncement(); loadMembers(); loadHistory(); }}
                            />

                            {action === "viewTeam" && teams && (
                                // Teams already on this incident aren't offered again — re-assigning one
                                // used to duplicate every member's roster row.
                                <TeamCard assigned={false} teams={teams.filter((t) => !assignedTeams.some((a) => a.id === t.id))} setTeams={setTeams} announcementId={id} handelViewTeam={handelViewTeam} onAssigned={refreshAndShowAssigned} />
                            )}
                            {action === "assignTeam" && members && assignedTeams && (
                                <TeamCard assigned={true} teams={assignedTeams} setTeams={setAssignedTeams} announcementId={id} handelViewTeam={handelViewTeam} onAssignTeam={handleAssignTeam} />
                            )}
                            {action === "viewMembers" && assignedTeams && (
                                <div>
                                    <Button variant="ghost" size="sm" className="mb-4" onClick={() => setAction("assignTeam")}>
                                        &larr; Back to Teams
                                    </Button>
                                    <MemberCard members={teamMembers} />
                                </div>
                            )}
                            {action === "viewResponses" && <ResponseSummary members={members} />}
                            {action === "roster" && <ResponderRoster members={members} onUpdated={loadMembers} />}
                        </div>
                    )}

                    {tab === 'patients' && (
                        <DataTable
                            headCells={[
                                { id: 'id', label: 'Patient ID' },
                                { id: 'triage', label: 'Triage', render: (row) => <Badge>{row.triage}</Badge> },
                                { id: 'zone', label: 'Zone', render: (row) => (row.zone ? <Badge variant={ZONE_VARIANTS[row.zone]}>{row.zone}</Badge> : '—') },
                                { id: 'rso', label: 'RSO', render: (row) => { const s = rsoStatusOf(row); return <Badge variant={RSO_VARIANTS[s]}>{s}</Badge>; } },
                                { id: 'gender', label: 'Gender', render: (row) => row.gender || 'Unknown' },
                                { id: 'vitals', label: 'Vitals' },
                                { id: 'cbrne', label: 'CBRNE' },
                                { id: 'decon', label: 'Decon', render: (row) => <Badge>{row.decon}</Badge> },
                                { id: 'destination', label: 'Destination' },
                                { id: 'sync', label: 'Sync', render: (row) => <Badge>{row.sync}</Badge> },
                            ]}
                            rows={patients}
                            onView={(row) => navigate(`/patient/${row.recordId}`)}
                            emptyLabel="No patient records synced for this incident yet."
                        />
                    )}

                    {tab === 'hospitals' && (
                        <div>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                                <div>
                                    <h4 className="font-bold text-neutral-900 dark:text-neutral-50">Hospitals Prepared for This Incident</h4>
                                    <p className="text-xs text-neutral-500 mt-1 dark:text-neutral-500">Notify and track receiving hospitals, independent of team assignment.</p>
                                </div>
                                <Button variant="primary" size="md" onClick={openAssignHospital}>
                                    Assign Hospital
                                </Button>
                            </div>
                            <HospitalRoster hospitals={hospitals} onUpdated={loadHospitals} />
                        </div>
                    )}

                    {tab === 'handoff' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="strategy-card">
                                <h4 className="font-bold text-neutral-900 dark:text-neutral-50">QR/NFC Handoff</h4>
                                <p className="text-sm text-neutral-500 mt-2 dark:text-neutral-500">Generate patient transfer packages for the ambulance / receiving team.</p>
                                <button className="action-button mt-4" onClick={() => toast.success('QR/NFC package generated')}>
                                    <QrCode className="w-4 h-4 mr-2" /> Generate
                                </button>
                            </div>
                            <div className="strategy-card">
                                <h4 className="font-bold text-neutral-900 dark:text-neutral-50">PDF Report</h4>
                                <p className="text-sm text-neutral-500 mt-2 dark:text-neutral-500">Export command, team, patient, decon and stakeholder report.</p>
                                <button className="action-button mt-4" onClick={() => toast.success('PDF report exported')}>
                                    <FileText className="w-4 h-4 mr-2" /> Export
                                </button>
                            </div>
                            <div className="strategy-card">
                                <h4 className="font-bold text-neutral-900 dark:text-neutral-50">After Action</h4>
                                <p className="text-sm text-neutral-500 mt-2 dark:text-neutral-500">Capture drill/event performance and improvement actions.</p>
                                <button className="action-button mt-4" onClick={() => toast.success('After-action record opened')}>
                                    <ClipboardList className="w-4 h-4 mr-2" /> Open
                                </button>
                            </div>
                        </div>
                    )}

                    {tab === 'history' && (
                        <div className="space-y-2.5">
                            {history.length === 0 && (
                                <div className="strategy-card text-center text-sm text-neutral-400 py-10 dark:text-neutral-500">
                                    No status changes recorded yet.
                                </div>
                            )}
                            {history.map((entry) => {
                                const notes = historyNotes(entry.details);
                                return (
                                    <div key={entry.id} className="timeline-row">
                                        <time>{new Date(entry.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</time>
                                        <div>
                                            <p className="text-sm font-bold text-neutral-900 dark:text-neutral-50">
                                                {HISTORY_LABELS[entry.action] || entry.action}
                                            </p>
                                            <p className="text-xs text-neutral-500 mt-0.5 dark:text-neutral-400">
                                                {entry.memberEmail || 'System'} &middot; {new Date(entry.createdAt).toLocaleString()}
                                            </p>
                                            {notes && (
                                                <p className="text-xs text-neutral-600 mt-1 dark:text-neutral-300">{notes}</p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </Card>

            <Modal
                open={closeOpen}
                onClose={() => setCloseOpen(false)}
                title="Close Event"
                subtitle="Stand down teams and archive this event. Assigned members are notified."
                confirmLabel="Close Event"
                onConfirm={handleClose}
            >
                <div>
                    <label className="form-label" htmlFor="close-notes">Closure Notes</label>
                    <textarea
                        id="close-notes"
                        className="form-input"
                        rows={4}
                        placeholder="Outcome summary, lessons learned, remaining actions..."
                        value={closeNotes}
                        onChange={(e) => setCloseNotes(e.target.value)}
                    />
                </div>
            </Modal>

            <Modal
                open={reopenOpen}
                onClose={() => setReopenOpen(false)}
                title="Reopen Event"
                subtitle="Resume this event — teams are re-escalated and notified again."
                confirmLabel="Reopen Event"
                onConfirm={handleReopen}
            >
                <div>
                    <label className="form-label" htmlFor="reopen-notes">Reason (optional)</label>
                    <textarea
                        id="reopen-notes"
                        className="form-input"
                        rows={4}
                        placeholder="Why is this event being reopened..."
                        value={reopenNotes}
                        onChange={(e) => setReopenNotes(e.target.value)}
                    />
                </div>
            </Modal>

            <Modal
                open={activationGuardOpen}
                onClose={() => setActivationGuardOpen(false)}
                title="Not Ready to Activate"
                subtitle="An incident goes live only with teams, a hospital and a team leader assigned."
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setActivationGuardOpen(false)}>Cancel</Button>
                        {hospitals.length === 0 && (
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setActivationGuardOpen(false);
                                    setTab('hospitals');
                                    openAssignHospital();
                                }}
                            >
                                Assign Hospital
                            </Button>
                        )}
                        {assignedTeams.length === 0 && (
                            <Button
                                variant="primary"
                                onClick={() => {
                                    setActivationGuardOpen(false);
                                    setTab('teams');
                                    handleAssignTeam();
                                }}
                            >
                                Assign Team
                            </Button>
                        )}
                        {assignedTeams.length > 0 && !announcement.teamLeader && (
                            <Button
                                variant="primary"
                                onClick={() => {
                                    setActivationGuardOpen(false);
                                    setTab('teams');
                                }}
                            >
                                Assign Team Leader
                            </Button>
                        )}
                    </>
                }
            >
                <ul className="list-disc pl-5 space-y-1 text-sm text-neutral-600 dark:text-neutral-300">
                    {assignedTeams.length === 0 && <li>No team is assigned to this incident.</li>}
                    {hospitals.length === 0 && <li>No hospital is prepared for this incident.</li>}
                    {!announcement.teamLeader && <li>No team leader is assigned to lead this incident on scene.</li>}
                </ul>
            </Modal>

            <Modal
                open={locationPickerOpen}
                onClose={() => setLocationPickerOpen(false)}
                title="Capture Location"
                subtitle="Search an address or click the map to drop a pin."
                size="lg"
                confirmLabel={savingLocation ? 'Saving…' : 'Save Location'}
                onConfirm={handleSaveLocation}
            >
                {pendingLocation && (
                    <div className="space-y-4">
                        <ExactLocation
                            onSelect={({ location, lat, lng }) => setPendingLocation({ location, lat, lng })}
                            label="Search Address"
                            placeholder="Type an address..."
                        />
                        <div className="rounded-xl border border-neutral-200 overflow-hidden h-[360px] dark:border-neutral-700">
                            <GoogleMapReact
                                bootstrapURLKeys={{ key: 'AIzaSyCI8O91FfQgfevadyNo9pKNt52MT5RZp_4' }}
                                center={{ lat: pendingLocation.lat, lng: pendingLocation.lng }}
                                defaultZoom={13}
                                options={{ styles: mode === 'dark' ? DARK_MAP_STYLE : LIGHT_MAP_STYLE }}
                                onClick={handlePickOnMap}
                            >
                                <IncidentMarker lat={pendingLocation.lat} lng={pendingLocation.lng} text={pendingLocation.location} />
                            </GoogleMapReact>
                        </div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">
                            <b className="text-neutral-700 dark:text-neutral-200">Selected:</b>{' '}
                            {resolvingAddress ? 'Resolving address…' : (pendingLocation.location || '—')}
                            {' '}({pendingLocation.lat.toFixed(6)}, {pendingLocation.lng.toFixed(6)})
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                open={assignResponderOpen}
                onClose={() => setAssignResponderOpen(false)}
                title="Assign Responder"
                subtitle="Assign individual member(s) to this incident, independent of their team."
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setAssignResponderOpen(false)}>Cancel</Button>
                        <Button
                            variant="primary"
                            onClick={handleAssignResponders}
                            disabled={selectedResponderIds.length === 0 || assigningResponders}
                        >
                            {assigningResponders
                                ? 'Assigning…'
                                : `Assign (${selectedResponderIds.length})`}
                        </Button>
                    </>
                }
            >
                <div className="space-y-3">
                    <div className="relative">
                        <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            className="form-input pl-9"
                            placeholder="Search members by name..."
                            value={responderSearch}
                            onChange={(e) => setResponderSearch(e.target.value)}
                        />
                    </div>

                    <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100 max-h-96 overflow-y-auto dark:border-neutral-700 dark:divide-neutral-800">
                        {loadingAllMembers && (
                            <p className="text-sm text-neutral-400 text-center py-8 dark:text-neutral-500">Loading members…</p>
                        )}
                        {!loadingAllMembers && availableResponders.length === 0 && (
                            <p className="text-sm text-neutral-400 text-center py-8 dark:text-neutral-500">
                                {responderSearch ? 'No members match your search.' : 'All members are already assigned to this incident.'}
                            </p>
                        )}
                        {!loadingAllMembers && availableResponders.map((m) => {
                            const checked = selectedResponderIds.includes(m.id);
                            return (
                                <label
                                    key={m.id}
                                    className="flex items-center gap-3 px-3.5 py-2.5 cursor-pointer hover:bg-neutral-50 dark:hover:bg-ink-800"
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleResponder(m.id)}
                                        className="w-4 h-4 rounded border-neutral-300 text-seha-500 focus:ring-seha-500 dark:border-neutral-600"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-neutral-900 truncate dark:text-neutral-50">{m.fullname}</p>
                                        {m.mobile && <p className="text-xs text-neutral-500 dark:text-neutral-500">{m.mobile}</p>}
                                    </div>
                                    {m.role?.role_name && (
                                        <Badge variant="neutral">{ROLE_LABELS[m.role.role_name] || m.role.role_name}</Badge>
                                    )}
                                </label>
                            );
                        })}
                    </div>
                </div>
            </Modal>

            <Modal
                open={assignHospitalOpen}
                onClose={() => setAssignHospitalOpen(false)}
                title="Assign Hospital"
                subtitle="Notify and prepare hospital(s) for this incident, independent of team assignment."
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setAssignHospitalOpen(false)}>Cancel</Button>
                        <Button
                            variant="primary"
                            onClick={handleAssignHospitals}
                            disabled={selectedHospitalIds.length === 0 || assigningHospitals}
                        >
                            {assigningHospitals
                                ? 'Assigning…'
                                : `Assign (${selectedHospitalIds.length})`}
                        </Button>
                    </>
                }
            >
                <div className="space-y-3">
                    <div className="relative">
                        <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            className="form-input pl-9"
                            placeholder="Search hospitals by name..."
                            value={hospitalSearch}
                            onChange={(e) => setHospitalSearch(e.target.value)}
                        />
                    </div>

                    <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100 max-h-96 overflow-y-auto dark:border-neutral-700 dark:divide-neutral-800">
                        {loadingAllHospitals && (
                            <p className="text-sm text-neutral-400 text-center py-8 dark:text-neutral-500">Loading hospitals…</p>
                        )}
                        {!loadingAllHospitals && availableHospitals.length === 0 && (
                            <p className="text-sm text-neutral-400 text-center py-8 dark:text-neutral-500">
                                {hospitalSearch ? 'No hospitals match your search.' : 'All hospitals are already assigned to this incident.'}
                            </p>
                        )}
                        {!loadingAllHospitals && availableHospitals.map((h) => {
                            const checked = selectedHospitalIds.includes(h.id);
                            return (
                                <label
                                    key={h.id}
                                    className="flex items-center gap-3 px-3.5 py-2.5 cursor-pointer hover:bg-neutral-50 dark:hover:bg-ink-800"
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleHospital(h.id)}
                                        className="w-4 h-4 rounded border-neutral-300 text-seha-500 focus:ring-seha-500 dark:border-neutral-600"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-neutral-900 truncate dark:text-neutral-50">{h.name}</p>
                                        {h.code && <p className="text-xs text-neutral-500 dark:text-neutral-500">{h.code}</p>}
                                    </div>
                                    {h.bedCapacity != null && (
                                        <Badge variant="neutral">{h.bedCapacity} beds</Badge>
                                    )}
                                </label>
                            );
                        })}
                    </div>
                </div>
            </Modal>
        </Sidebar>
    );
}

export default Incident;
